import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';

interface BrandProtectedRouteProps {
  children: React.ReactNode;
  requireBrandUser?: boolean;
}

const BrandProtectedRoute: React.FC<BrandProtectedRouteProps> = ({ 
  children,
  requireBrandUser = true
}) => {
  const { user, profile, loading } = useAuth();
  const { brandSlug } = useParams();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to={`/brand/${brandSlug}/login`} replace />;
  }

  // Allow admins to access any brand - super permissive for admins
  const isAdmin = profile?.role === 'super_admin' || 
                  profile?.role === 'brand_admin' || 
                  profile?.role === 'org_admin';

  // Admins can always access any brand, no further checks needed
  if (isAdmin) {
    return <>{children}</>;
  }

  // For brand user routes, ensure user has brand_user role
  if (requireBrandUser && profile?.role !== 'brand_user') {
    return <Navigate to="/admin" replace />;
  }

  // Ensure user has a brand_id (belongs to a brand)
  if (requireBrandUser && !profile?.brand_id) {
    return <Navigate to="/admin" replace />;
  }

  return <>{children}</>;
};

export default BrandProtectedRoute;